import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@pp/shared/utils/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'error';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm';

    const variants = {
      primary:
        'bg-primary text-primary-foreground hover:bg-primary-dark hover:shadow-md active:scale-[0.98] focus:ring-primary',
      secondary:
        'bg-secondary text-secondary-foreground hover:bg-secondary-dark hover:shadow-md active:scale-[0.98] focus:ring-secondary',
      outline:
        'border-2 border-border text-text bg-transparent hover:bg-surface hover:border-border-strong active:scale-[0.98] focus:ring-primary',
      ghost: 'text-text bg-transparent hover:bg-surface/80 active:scale-[0.98] focus:ring-primary shadow-none',
      error: 'bg-error text-white hover:bg-error-dark hover:shadow-md active:scale-[0.98] focus:ring-error',
    };

    const sizes = {
      sm: 'px-3 py-2 text-sm gap-1.5 min-h-[36px]',
      md: 'px-4 sm:px-5 py-2.5 text-sm sm:text-base gap-2 min-h-[44px]',
      lg: 'px-5 sm:px-6 py-3 text-base sm:text-lg gap-2.5 min-h-[48px]',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" size={size === 'sm' ? 14 : 16} />}
        {!loading && leftIcon}
        {children}
        {!loading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
